import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useLoginUser, useMyPermissions } from "http-hooks/auth/useAuth";
import { useAuthStore, useFinancialYearStore } from "store";
import useGlobalStore from "store/global";

const getCompanyStateIdFromToken = (token) => {
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const stateId = payload?.company?.stateId ?? payload?.company?.stateid;
    const n = Number(stateId);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
};

const getCompanyHasGstFromToken = (token) => {
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return Boolean(payload?.company?.gst?.trim());
  } catch {
    return false;
  }
};

const getUserGodownIdFromToken = (token) => {
  if (!token) return 0;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const godownId = Number(payload?.user_godown || 0);
    return Number.isFinite(godownId) && godownId > 0 ? godownId : 0;
  } catch {
    return 0;
  }
};

const useAuth = () => {
  const navigate = useNavigate();
  
  // Simple store selectors
  const username = useAuthStore((state) => state.username);
  const token = useAuthStore((state) => state.token);
  const companyId = useAuthStore((state) => state.companyId);
  const companyName = useAuthStore((state) => state.companyName);
  const agttp = useAuthStore((state) => state.agttp);
  const user_type = useAuthStore((state) => state.user_type);
  const accountype_temp = useAuthStore((state) => state.accountype_temp);
  const permissions = useAuthStore((state) => state.permissions);
  const isAuthenticated = useAuthStore((state) => !!state.token);
  const companyStateId = useMemo(() => getCompanyStateIdFromToken(token), [token]);
  const companyHasGst = useMemo(() => getCompanyHasGstFromToken(token), [token]);
  const userGodownId = useMemo(() => getUserGodownIdFromToken(token), [token]);
  const getUserTypeLabel = useAuthStore((state) => state.getUserTypeLabel);
  const hasAccess = useAuthStore((state) => state.hasAccess);
  const getLevel = useAuthStore((state) => state.getLevel);
  const clearAuthData = useAuthStore((state) => state.clearAuthData);
  const setToken = useAuthStore((state) => state.setToken);
  const setPermissions = useAuthStore((state) => state.setPermissions);
  const updateUserType = useAuthStore((state) => state.updateUserType);
  const clearFinancialYear = useFinancialYearStore((state) => state.clearFinancialYear);
  const clearGlobalState = useGlobalStore((state) => state.clearGlobalState);

  // Fetch permissions from DB (real-time)
  const { data: dbPermissions } = useMyPermissions();

  useEffect(() => {
    if (dbPermissions) {
      setPermissions(dbPermissions);
    }
  }, [dbPermissions, setPermissions]);

  // Simple login mutation
  const loginMutation = useLoginUser();

  const login = async (credentials) => {
    try {
      await loginMutation.mutateAsync(credentials);
      setTimeout(() => {
        navigate("/");
      }, 500);
    } catch (error) {
      console.error("Login failed:", error);
    }
  };

  const logout = () => {
    clearGlobalState();
    clearFinancialYear();
    clearAuthData();
    window.location.href = `/login/${companyId || ''}`;
  };

  return {
    // Auth state
    username,
    token,
    companyId,
    companyName,
    companyStateId,
    companyHasGst,
    userGodownId,
    agttp,
    user_type,
    accountype_temp,
    permissions,
    isAuthenticated,
    
    // Permission helpers
    hasAccess,
    getLevel,
    
    // Helper functions
    getUserTypeLabel,
    
    // Auth actions
    login,
    logout,
    setToken,
    updateUserType,
    
    // Login mutation state
    isLoggingIn: loginMutation.isPending,
    loginError: loginMutation.error,
  };
};

export default useAuth; 